/**
 * ConfigurationPanel — exposes physics simulation parameters to the user.
 *
 * Uses the actual SimulationConfig types. Validates inputs before running
 * via the existing validateSimulationConfig function. Beginner-friendly
 * settings are shown first; advanced settings are collapsible.
 */

import { useState } from "react"
import {
  DefaultConfig,
  type SimulationConfig,
  type Environment,
  type RocketParams,
} from "@/lib/physics"
import { validateSimulationConfig } from "@/lib/physics/rocket"
import { Panel, SectionTitle } from "@/components/ui"

interface ConfigurationPanelProps {
  onRun: (config: SimulationConfig) => void
}

interface FormValues {
  // Environment
  gravity: number
  seaLevelDensity: number
  scaleHeight: number
  // Rocket
  dryMass: number
  maxFuel: number
  maxThrust: number
  fuelFlowRate: number
  dragCoefficient: number
  referenceArea: number
  inertiaY: number
  centerOfMassOffset: number
  maxGimbalAngle: number
  // Initial state
  initialZ: number
  initialX: number
  initialVX: number
  initialVZ: number
  initialFuel: number
  initialTheta: number
  initialOmega: number
  // Simulation
  landingTargetX: number
  maxTouchdownVerticalSpeed: number
  maxTouchdownHorizontalSpeed: number
  maxHorizontalError: number
  maxTouchdownAttitude: number
  maxTouchdownAngularRate: number
  descentRate: number
  flareAltitude: number
}

const DEFAULT_FORM: FormValues = {
  gravity: DefaultConfig.environment.gravity,
  seaLevelDensity: DefaultConfig.environment.seaLevelDensity,
  scaleHeight: DefaultConfig.environment.scaleHeight,
  dryMass: DefaultConfig.rocket.dryMass,
  maxFuel: DefaultConfig.rocket.maxFuel,
  maxThrust: DefaultConfig.rocket.maxThrust,
  fuelFlowRate: DefaultConfig.rocket.fuelFlowRate,
  dragCoefficient: DefaultConfig.rocket.dragCoefficient,
  referenceArea: DefaultConfig.rocket.referenceArea,
  inertiaY: DefaultConfig.rocket.inertiaY,
  centerOfMassOffset: DefaultConfig.rocket.centerOfMassOffset,
  maxGimbalAngle: DefaultConfig.maxGimbalAngle,
  initialZ: DefaultConfig.initialState.z,
  initialX: DefaultConfig.initialState.x,
  initialVX: DefaultConfig.initialState.vx,
  initialVZ: DefaultConfig.initialState.vz,
  initialFuel: DefaultConfig.initialState.fuel,
  initialTheta: DefaultConfig.initialState.theta,
  initialOmega: DefaultConfig.initialState.omega,
  landingTargetX: DefaultConfig.landingTargetX,
  maxTouchdownVerticalSpeed: DefaultConfig.maxTouchdownVerticalSpeed,
  maxTouchdownHorizontalSpeed: DefaultConfig.maxTouchdownHorizontalSpeed,
  maxHorizontalError: DefaultConfig.maxHorizontalError,
  maxTouchdownAttitude: DefaultConfig.maxTouchdownAttitude,
  maxTouchdownAngularRate: DefaultConfig.maxTouchdownAngularRate,
  descentRate: DefaultConfig.descentRate,
  flareAltitude: DefaultConfig.flareAltitude,
}

export function ConfigurationPanel({ onRun }: ConfigurationPanelProps) {
  const [form, setForm] = useState<FormValues>(DEFAULT_FORM)
  const [advanced, setAdvanced] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetToDefaults = () => {
    setForm(DEFAULT_FORM)
    setError(null)
  }

  const buildConfig = (): SimulationConfig | null => {
    try {
      const env: Environment = {
        gravity: form.gravity,
        seaLevelDensity: form.seaLevelDensity,
        scaleHeight: form.scaleHeight,
        density: (z: number) =>
          form.seaLevelDensity *
          Math.exp(-Math.max(z, 0) / (form.scaleHeight === Infinity ? Infinity : form.scaleHeight)),
      }

      const rocket: RocketParams = {
        dryMass: form.dryMass,
        maxFuel: form.maxFuel,
        maxThrust: form.maxThrust,
        fuelFlowRate: form.fuelFlowRate,
        dragCoefficient: form.dragCoefficient,
        referenceArea: form.referenceArea,
        inertiaY: form.inertiaY,
        centerOfMassOffset: form.centerOfMassOffset,
      }

      const config: SimulationConfig = {
        environment: env,
        rocket,
        initialState: {
          t: 0,
          x: form.initialX,
          z: form.initialZ,
          vx: form.initialVX,
          vz: form.initialVZ,
          fuel: form.initialFuel,
          theta: form.initialTheta,
          omega: form.initialOmega,
        },
        maxGimbalAngle: form.maxGimbalAngle,
        physicsDt: DefaultConfig.physicsDt,
        outputDt: DefaultConfig.outputDt,
        maxDuration: DefaultConfig.maxDuration,
        landingTargetX: form.landingTargetX,
        maxTouchdownVerticalSpeed: form.maxTouchdownVerticalSpeed,
        maxTouchdownHorizontalSpeed: form.maxTouchdownHorizontalSpeed,
        maxHorizontalError: form.maxHorizontalError,
        maxTouchdownAttitude: form.maxTouchdownAttitude,
        maxTouchdownAngularRate: form.maxTouchdownAngularRate,
        descentRate: form.descentRate,
        flareAltitude: form.flareAltitude,
      }

      validateSimulationConfig(config)
      return config
    } catch (err) {
      setError((err as Error).message)
      return null
    }
  }

  const handleRun = () => {
    setError(null)
    const config = buildConfig()
    if (config) {
      onRun(config)
    }
  }

  const numberField = (
    label: string,
    value: number,
    onChange: (v: number) => void,
    unit?: string,
    min?: number,
    max?: number
  ) => (
    <div className="space-y-1">
      <label className="text-[0.625rem] font-medium text-dim uppercase tracking-wider">
        {label}
      </label>
      <div className="flex items-end gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          className="w-full bg-surface border border-border rounded-sm px-2.5 py-1.5 text-xs font-mono text-ink focus:border-cyan-500 focus:outline-none"
        />
        {unit && <span className="text-[0.625rem] text-dim">{unit}</span>}
      </div>
    </div>
  )

  return (
    <Panel title="Simulation Configuration" padded={false}>
      <div className="p-3 space-y-4">
        {error && (
          <div className="p-2 rounded-sm bg-danger-500/10 border border-danger-500/30 text-[0.625rem] text-danger-400">
            {error}
          </div>
        )}

        {/* Beginner settings */}
        <SectionTitle>Mission Parameters</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {numberField("Initial Altitude (m)", form.initialZ, (v) => setForm({ ...form, initialZ: v }), "m", 0)}
          {numberField("Initial X Pos (m)", form.initialX, (v) => setForm({ ...form, initialX: v }), "m")}
          {numberField("Initial VX (m/s)", form.initialVX, (v) => setForm({ ...form, initialVX: v }), "m/s")}
          {numberField("Initial VZ (m/s)", form.initialVZ, (v) => setForm({ ...form, initialVZ: v }), "m/s")}
          {numberField("Initial Pitch (rad)", form.initialTheta, (v) => setForm({ ...form, initialTheta: v }), "rad")}
          {numberField("Initial Pitch Rate (rad/s)", form.initialOmega, (v) => setForm({ ...form, initialOmega: v }), "rad/s")}
          {numberField("Initial Fuel (kg)", form.initialFuel, (v) => setForm({ ...form, initialFuel: v }), "kg", 0, form.maxFuel)}
          {numberField("Landing Target X (m)", form.landingTargetX, (v) => setForm({ ...form, landingTargetX: v }), "m")}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={resetToDefaults}
            className="px-2.5 py-1.5 rounded-sm text-xs font-medium text-dim hover:text-ink hover:bg-surface/50 transition"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleRun}
            disabled={!!error}
            className="px-3 py-1.5 rounded-sm text-xs font-medium bg-cyan-500 hover:bg-cyan-400 text-black shadow-glow transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Run Simulation
          </button>
        </div>

        {/* Advanced settings (collapsible) */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between cursor-pointer mb-2" onClick={() => setAdvanced(!advanced)}>
            <span className="text-[0.625rem] font-medium text-muted uppercase tracking-wider">Advanced Configuration</span>
            <span className="text-[0.625rem] text-dim">{advanced ? "▲" : "▼"}</span>
          </div>
          {advanced && (
            <div className="space-y-4">
              <SectionTitle>Environment</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {numberField("Gravity (m/s²)", form.gravity, (v) => setForm({ ...form, gravity: v }), "m/s²", 0)}
                {numberField("Sea-Level Density (kg/m³)", form.seaLevelDensity, (v) => setForm({ ...form, seaLevelDensity: v }), "kg/m³", 0)}
                {numberField("Scale Height (m)", form.scaleHeight, (v) => setForm({ ...form, scaleHeight: v }), "m", 1)}
              </div>

              <SectionTitle>Rocket Parameters</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {numberField("Dry Mass (kg)", form.dryMass, (v) => setForm({ ...form, dryMass: v }), "kg", 0.1)}
                {numberField("Max Fuel (kg)", form.maxFuel, (v) => setForm({ ...form, maxFuel: v }), "kg", 0.1)}
                {numberField("Max Thrust (N)", form.maxThrust, (v) => setForm({ ...form, maxThrust: v }), "N", 0.1)}
                {numberField("Fuel Flow Rate (kg/s)", form.fuelFlowRate, (v) => setForm({ ...form, fuelFlowRate: v }), "kg/s", 0)}
                {numberField("Drag Coefficient", form.dragCoefficient, (v) => setForm({ ...form, dragCoefficient: v }), "", 0)}
                {numberField("Reference Area (m²)", form.referenceArea, (v) => setForm({ ...form, referenceArea: v }), "m²", 0.01)}
                {numberField("Pitch Inertia (kg·m²)", form.inertiaY, (v) => setForm({ ...form, inertiaY: v }), "kg·m²", 0.1)}
                {numberField("CoM Offset (m)", form.centerOfMassOffset, (v) => setForm({ ...form, centerOfMassOffset: v }), "m", 0)}
                {numberField("Max Gimbal (rad)", form.maxGimbalAngle, (v) => setForm({ ...form, maxGimbalAngle: v }), "rad", 0.01)}
              </div>

              <SectionTitle>Landing Criteria</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {numberField("Max TD Vertical Speed (m/s)", form.maxTouchdownVerticalSpeed, (v) => setForm({ ...form, maxTouchdownVerticalSpeed: v }), "m/s", 0)}
                {numberField("Max TD Horizontal Speed (m/s)", form.maxTouchdownHorizontalSpeed, (v) => setForm({ ...form, maxTouchdownHorizontalSpeed: v }), "m/s", 0)}
                {numberField("Max Horizontal Error (m)", form.maxHorizontalError, (v) => setForm({ ...form, maxHorizontalError: v }), "m", 0)}
                {numberField("Max TD Attitude (rad)", form.maxTouchdownAttitude, (v) => setForm({ ...form, maxTouchdownAttitude: v }), "rad", 0)}
                {numberField("Max TD Angular Rate (rad/s)", form.maxTouchdownAngularRate, (v) => setForm({ ...form, maxTouchdownAngularRate: v }), "rad/s", 0)}
                {numberField("Descent Rate (m/s)", form.descentRate, (v) => setForm({ ...form, descentRate: v }), "m/s", 0)}
                {numberField("Flare Altitude (m)", form.flareAltitude, (v) => setForm({ ...form, flareAltitude: v }), "m", 0)}
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}
